import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { suspendApp, type AdminRegistryApp } from '@/api/console';
import { Button, FormField, Input, Modal, useToast } from '@/components/ui';

export interface SuspendAppModalProps {
    /** The app pending suspension — `null` closes the modal. */
    app: AdminRegistryApp | null;
    onClose: () => void;
}

/**
 * EAA-1884 (closes the EAA-1817 plan's gap #1, "Việc cần cân nhắc" card):
 * `POST /admin/apps/{code}/suspend` demands a non-blank `reason`
 * (`AdminAppSuspendSerializer`) — validated client-side too so a blank
 * submit never round-trips to the server for a 400 it can already predict.
 */
export function SuspendAppModal({ app, onClose }: SuspendAppModalProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();
    const [reason, setReason] = useState('');

    const suspendMutation = useMutation({
        mutationFn: (target: AdminRegistryApp) =>
            suspendApp(config.apiBase, accessToken as string, target.code, reason.trim()),
        onSuccess: (_data, target) => {
            toast.success(
                t('console.appDetail.overview.considerations.suspendModal.toastSuccess', {
                    name: target.name,
                })
            );
            handleClose();
            // EAA-1884: same invalidation set `FlipAppModal` uses for its own
            // enable/disable flip — the registry row, the launcher's
            // enabled-only list, and role-map sync-status's health counters
            // all derive from this same registry data.
            void queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
            void queryClient.invalidateQueries({ queryKey: ['apps-registry'] });
            void queryClient.invalidateQueries({ queryKey: ['role-map-sync-status'] });
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : undefined;
            toast.error(
                message || t('console.appDetail.overview.considerations.suspendModal.toastError')
            );
        },
    });

    function handleClose() {
        if (suspendMutation.isPending) return;
        setReason('');
        onClose();
    }

    const trimmedReason = reason.trim();
    const canSubmit = trimmedReason.length > 0;

    return (
        <Modal
            open={app !== null}
            onCancel={handleClose}
            title={t('console.appDetail.overview.considerations.suspendModal.title')}
            footer={
                <>
                    <Button onClick={handleClose} disabled={suspendMutation.isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        danger
                        type="primary"
                        onClick={() => app && suspendMutation.mutate(app)}
                        disabled={!canSubmit || suspendMutation.isPending}
                    >
                        {suspendMutation.isPending
                            ? t('console.appDetail.overview.considerations.suspendModal.pending')
                            : t('console.appDetail.overview.considerations.suspendModal.confirmButton')}
                    </Button>
                </>
            }
        >
            {app && (
                <>
                    <p>
                        {t('console.appDetail.overview.considerations.suspendModal.body', {
                            name: app.name,
                            code: app.code,
                        })}
                    </p>
                    <FormField
                        label={t('console.appDetail.overview.considerations.suspendModal.reasonLabel')}
                        required
                        hint={t('console.appDetail.overview.considerations.suspendModal.reasonHint')}
                    >
                        <Input.TextArea
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            rows={3}
                            placeholder={t(
                                'console.appDetail.overview.considerations.suspendModal.reasonPlaceholder'
                            )}
                        />
                    </FormField>
                </>
            )}
        </Modal>
    );
}

export default SuspendAppModal;
